/**
 * Token storage utilities for managing authentication tokens
 */

const ACCESS_TOKEN_KEY = "bt_b2b_access_token";
const REFRESH_TOKEN_KEY = "bt_b2b_refresh_token";
const USER_DATA_KEY = "bt_b2b_user_data";

/**
 * Storage interface for different environments
 */
interface StorageInterface {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Get the appropriate storage interface
 */
const getStorage = (): StorageInterface => {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }

  // Fallback for SSR or environments without localStorage
  return {
    getItem: () => null,
    setItem: () => {},
    removeItem: () => {},
  };
};

/**
 * Get the current access token
 */
export const getAccessToken = async (): Promise<string | null> => {
  try {
    const storage = getStorage();
    return storage.getItem(ACCESS_TOKEN_KEY);
  } catch (error) {
    console.error("Failed to get access token:", error);
    return null;
  }
};

/**
 * Set the access token
 * Dispatches bt_b2b_token_updated so AuthProvider (same tab) can update immediately.
 */
export const setAccessToken = (token: string): void => {
  try {
    const storage = getStorage();
    storage.setItem(ACCESS_TOKEN_KEY, token);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("bt_b2b_token_updated"));
    }
  } catch (error) {
    console.error("Failed to set access token:", error);
  }
};

/**
 * Clear the access token
 */
export const clearAccessToken = (): void => {
  try {
    const storage = getStorage();
    storage.removeItem(ACCESS_TOKEN_KEY);
  } catch (error) {
    console.error("Failed to clear access token:", error);
  }
};

/**
 * Get the refresh token
 */
export const getRefreshToken = (): string | null => {
  try {
    const storage = getStorage();
    return storage.getItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error("Failed to get refresh token:", error);
    return null;
  }
};

/**
 * Set the refresh token
 */
export const setRefreshToken = (token: string): void => {
  try {
    const storage = getStorage();
    storage.setItem(REFRESH_TOKEN_KEY, token);
  } catch (error) {
    console.error("Failed to set refresh token:", error);
  }
};

/**
 * Clear the refresh token
 */
export const clearRefreshToken = (): void => {
  try {
    const storage = getStorage();
    storage.removeItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error("Failed to clear refresh token:", error);
  }
};

/**
 * Clear all authentication data
 */
export const clearAuthData = (): void => {
  clearAccessToken();
  clearRefreshToken();
  clearUserData();
};

/**
 * Set user data
 */
export const setUserData = (userData: any): void => {
  try {
    const storage = getStorage();
    storage.setItem(USER_DATA_KEY, JSON.stringify(userData));
  } catch (error) {
    console.error("Failed to set user data:", error);
  }
};

/**
 * Get user data
 */
export const getUserData = (): any | null => {
  try {
    const storage = getStorage();
    const userData = storage.getItem(USER_DATA_KEY);
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error("Failed to get user data:", error);
    return null;
  }
};

/**
 * Clear user data
 */
export const clearUserData = (): void => {
  try {
    const storage = getStorage();
    storage.removeItem(USER_DATA_KEY);
  } catch (error) {
    console.error("Failed to clear user data:", error);
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = async (): Promise<boolean> => {
  const token = await getAccessToken();
  return !!token;
};
